import {
  css,
  MenuItem,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import {debounce, isEqual, isNumber} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {PAGE_SIZE_SELECT_OPTIONS} from 'src/js/common/constants';
import {GetStylesFunction} from 'src/js/common/types';

import {DlMuiSelect} from './DlMuiSelect';

const PAGE_SIZE_CONFIG = {
  MIN: 10,
  MAX: 500,
  STEP: 10,
};

function getValueWithinBounds(pageSize: number): number {
  return Math.min(
    Math.max(pageSize, PAGE_SIZE_CONFIG.MIN),
    PAGE_SIZE_CONFIG.MAX
  );
}

interface OwnProps {
  activeSize: number;
  onChange: (pageSize: number) => void;
  textBefore?: boolean;
  isBig?: boolean;
}

type Props = OwnProps;

export const PageSizeInput: React.FC<Props> = (props) => {
  const {activeSize, onChange, textBefore, isBig} = props;
  const theme = useTheme();
  const styles = getStyles({theme});

  const [pageSize, setPageSize] = useState<number>(activeSize);
  const [justForForcingRender, setJustForForcingRender] = useState<
    number | null
  >(null);

  const debouncedChangePage = useMemo(
    () => debounce(onChange, 500),
    [onChange]
  );

  const handleChange = useCallback(
    (_event: SelectChangeEvent<unknown>) => {
      const event = _event as SelectChangeEvent<string>;
      const newPageSize = parseInt(event.target.value, 10);
      if (!isEqual(newPageSize, activeSize)) {
        if (isNumber(newPageSize)) {
          const newBoundPageSize = getValueWithinBounds(newPageSize);
          setPageSize(newBoundPageSize);
          if (isEqual(newBoundPageSize, pageSize)) {
            setJustForForcingRender(Date.now());
          }
          debouncedChangePage(newBoundPageSize);
        } else if (!isEqual(newPageSize, pageSize)) {
          // return previous state
          setPageSize(pageSize);
        }
      }
    },
    [activeSize, debouncedChangePage, pageSize]
  );

  return (
    <span css={isBig ? styles.dlPageSizeInputBig : styles.dlPageSizeInput}>
      {textBefore && (
        <Typography
          data-name={'page-size-input--text-before'}
          variant={'caption'}
        >
          <FormattedMessage
            id={'common.pageSizeInput.entriesBefore'}
            defaultMessage={'entriesBefore'}
            description={'label in page size before numeric input'}
          />
        </Typography>
      )}
      <span css={styles.dlPageSizeInputTextBoxWrapper}>
        <DlMuiSelect
          data-name={'page-size-input--select'}
          onChange={handleChange}
          value={pageSize.toString()}
          inputCss={isBig ? styles.bigInput : undefined}
        >
          {PAGE_SIZE_SELECT_OPTIONS.map((value) => {
            return (
              <MenuItem key={value} value={value}>
                <Typography css={styles.menuLabel} variant={'value'}>
                  {value}
                </Typography>
              </MenuItem>
            );
          })}
        </DlMuiSelect>
      </span>
      {!textBefore && (
        <Typography
          data-name={'page-size-input--text-after'}
          variant={'caption'}
        >
          <FormattedMessage
            id={'common.pageSizeInput.entries'}
            defaultMessage={'entries'}
            description={'label in page size after numeric input'}
          />
        </Typography>
      )}
    </span>
  );
};

PageSizeInput.displayName = 'PageSizeInput';

const getStyles: GetStylesFunction = (options) => ({
  dlPageSizeInput: css`
    margin-right: var(--dlMarginSmall);
    display: inline-block;
  `,
  dlPageSizeInputBig: css`
    margin-right: var(--dlMarginExtraSmall);
    display: inline-block;
  `,
  dlPageSizeInputTextBoxWrapper: css`
    margin: 0 var(--dlPaddingSmaller);
  `,
  caretDownIcon: css`
    width: 16px;
    height: 16px;
    color: ${options?.theme?.palette.custom.coolGray['500']};
  `,
  menuItem: css`
    color: ${options?.theme?.palette.common.black};
  `,
  menuLabel: css`
    padding-left: 8px;
  `,
  bigInput: css`
    height: 32px;
    border-radius: 8px;
    svg {
      top: 6px;
    }
  `,
});
